'use client';

import React, { useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Search, Calendar as CalendarIcon, MapPin, Loader2, Users, ShieldCheck, ArrowRight, ChevronDown, Anchor, Clock } from 'lucide-react';
import { SearchState } from '@/types';
import { t } from '@/locales';

// Dynamic import for DateRangePicker (avoid SSR issues with FullCalendar)
const DateRangePicker = dynamic(() => import('@/components/ui/DateRangePicker'), {
  ssr: false,
  loading: () => null,
});

interface RentalHoursSettings {
  opening: string;
  closing: string;
  halfDayCutoff: string;
}

interface SearchSectionProps {
  onSearch: (filters: SearchState) => void;
  availableCities?: string[];
  availablePorts?: string[]; // Legacy - use availableCities instead
  rentalHours?: RentalHoursSettings;
}

const SearchSection: React.FC<SearchSectionProps> = ({
  onSearch,
  availableCities = [],
  availablePorts = [],
  rentalHours = { opening: '09:00', closing: '18:00', halfDayCutoff: '13:00' }
}) => {
  // Use cities if available, fallback to ports for backwards compatibility
  const cityOptions = availableCities.length > 0 ? availableCities : availablePorts;
  const [isSearching, setIsSearching] = useState(false);
  const [showPassengers, setShowPassengers] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPortDropdown, setShowPortDropdown] = useState(false);
  const [timeBlock, setTimeBlock] = useState<'FULL' | 'MORNING' | 'AFTERNOON'>('FULL');
  const containerRef = useRef<HTMLDivElement>(null);
  const portDropdownRef = useRef<HTMLDivElement>(null);

  const [searchState, setSearchState] = useState<SearchState>({
    location: '',
    startDate: '',
    startTime: rentalHours.opening,
    endDate: '',
    endTime: rentalHours.closing,
    passengers: 2,
    boatType: 'Tutte',
    noLicenseOnly: false
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowPassengers(false);
        setShowPortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fasce orarie derivate dalle rentalHours (opening / halfDayCutoff / closing).
  const cutoffHour = parseInt((rentalHours.halfDayCutoff || '13:00').split(':')[0]);
  const cutoffStr = rentalHours.halfDayCutoff || '13:00';
  const afternoonStart = `${String(cutoffHour + 1).padStart(2, '0')}:00`;

  const timeBlocks: Array<{ key: 'FULL' | 'MORNING' | 'AFTERNOON'; label: string; range: string; start: string; end: string }> = [
    { key: 'FULL',      label: 'Giornata intera', range: `${rentalHours.opening} – ${rentalHours.closing}`, start: rentalHours.opening, end: rentalHours.closing },
    { key: 'MORNING',   label: 'Mattina',         range: `${rentalHours.opening} – ${cutoffStr}`,            start: rentalHours.opening, end: cutoffStr },
    { key: 'AFTERNOON', label: 'Pomeriggio',      range: `${afternoonStart} – ${rentalHours.closing}`,       start: afternoonStart,      end: rentalHours.closing },
  ];

  const selectTimeBlock = (key: 'FULL' | 'MORNING' | 'AFTERNOON') => {
    setTimeBlock(key);
    const block = timeBlocks.find(b => b.key === key);
    if (!block) return;
    setSearchState(prev => ({ ...prev, startTime: block.start, endTime: block.end }));
  };

  const handleSearch = () => {
    if (!searchState.startDate) {
      setShowDatePicker(true);
      return;
    }
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      onSearch(searchState);
      setShowPassengers(false);
    }, 800);
  };

  const updateSearch = <K extends keyof SearchState>(field: K, value: SearchState[K]) => {
    setSearchState(prev => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (dates: { startDate: string; endDate: string }) => {
    setSearchState(prev => ({
      ...prev,
      startDate: dates.startDate,
      endDate: dates.endDate,
    }));
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  return (
    <>
      <div ref={containerRef} data-cid="search-section" className="relative w-full z-[100]">
        <div data-cid="search-section-card" className="bg-white/95 backdrop-blur-[40px] rounded-3xl shadow-[0_40px_100px_-20px_rgba(15,23,42,0.4)] border border-white/60 p-3 transition-all">
          {/* Main row - Date/Time and Search */}
          <div data-cid="search-section-row" className="flex flex-col lg:flex-row items-stretch lg:items-center justify-center gap-2">

            {/* Left section: Location + Date/Time */}
            <div data-cid="search-section-left" className="flex-1 flex flex-col sm:flex-row gap-2">
              {/* Città Dropdown */}
              <div data-cid="search-city" className="flex-1 min-w-0 relative" ref={portDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowPortDropdown(!showPortDropdown)}
                  className={`w-full h-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-left ${showPortDropdown ? 'bg-blue-50 ring-2 ring-blue-100' : 'bg-slate-50 hover:bg-white border border-transparent hover:border-blue-100'}`}
                >
                  <MapPin className="w-5 h-5 text-blue-600 shrink-0" />
                  <div className="flex-grow min-w-0">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('searchSection.city')}</p>
                    <span className={`text-sm font-bold truncate block ${searchState.location ? 'text-slate-800' : 'text-slate-400'}`}>
                      {searchState.location || t('searchSection.allCities')}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${showPortDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showPortDropdown && (
                  <div className="absolute top-[calc(100%+8px)] left-0 w-full min-w-[200px] bg-white rounded-2xl shadow-[0_50px_100px_-20px_rgba(15,23,42,0.3)] border border-slate-100 py-2 z-[200] animate-in fade-in zoom-in-95 duration-200 max-h-64 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        updateSearch('location', '');
                        setShowPortDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 ${!searchState.location ? 'bg-blue-50 text-blue-600' : 'text-slate-700'}`}
                    >
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="font-semibold">{t('searchSection.allCitiesLong')}</span>
                    </button>
                    {cityOptions.map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => {
                          updateSearch('location', city);
                          setShowPortDropdown(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 ${searchState.location === city ? 'bg-blue-50 text-blue-600' : 'text-slate-700'}`}
                      >
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span className="font-semibold">{city}</span>
                      </button>
                    ))}
                    {cityOptions.length === 0 && (
                      <div className="px-4 py-2.5 text-slate-400 text-sm text-center">
                        {t('searchSection.noCities')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Date Selection */}
              <div data-cid="search-dates" className="flex-1">
                <button
                  data-cid="search-dates-btn"
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full h-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-50 hover:bg-white border border-transparent hover:border-blue-100 transition-all text-left group"
                >
                  <CalendarIcon className="w-5 h-5 text-blue-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('searchSection.dates')}</p>
                    <div className="flex items-center gap-1 text-sm font-bold">
                      <span className={searchState.startDate ? 'text-slate-800' : 'text-slate-400'}>
                        {formatDateDisplay(searchState.startDate) || t('searchSection.dateStart')}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                      <span className={searchState.endDate ? 'text-slate-800' : 'text-slate-400'}>
                        {formatDateDisplay(searchState.endDate) || t('searchSection.dateEnd')}
                      </span>
                    </div>
                  </div>
                </button>
              </div>

              {/* Fasce orarie (select) */}
              <div data-cid="search-timeblock" className="flex-[1.4] min-w-[260px]">
                <div className="relative w-full h-full">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600" />
                  <select
                    value={timeBlock}
                    onChange={(e) => selectTimeBlock(e.target.value as 'FULL' | 'MORNING' | 'AFTERNOON')}
                    className="w-full h-full pl-10 pr-7 py-3 rounded-2xl bg-slate-50 hover:bg-white border border-transparent hover:border-blue-100 transition-all text-sm font-bold text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-100 truncate"
                    aria-label={t('searchSection.times')}
                  >
                    {timeBlocks.map(b => (
                      <option key={b.key} value={b.key}>
                        {b.label} · {b.range}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>

            </div>

            {/* Right section: Passengers + License + Search — centrali orizzontali */}
            <div
              data-cid="search-section-right"
              className="flex flex-row gap-2 lg:shrink-0 justify-center items-stretch w-full lg:w-auto"
            >
              {/* Passengers */}
              <div data-cid="search-passengers" className="flex-1 lg:w-auto relative">
                <button
                  data-cid="search-passengers-btn"
                  type="button"
                  onClick={() => setShowPassengers(!showPassengers)}
                  className={`w-full h-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-left ${showPassengers ? 'bg-blue-50 ring-2 ring-blue-100' : 'bg-slate-50 hover:bg-white border border-transparent hover:border-blue-100'}`}
                >
                  <Users className="w-5 h-5 text-blue-600 shrink-0" />
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('searchSection.guests')}</p>
                    <span className="text-sm font-bold text-slate-800">{searchState.passengers}</span>
                  </div>
                </button>

                {showPassengers && (
                  <div className="absolute top-[calc(100%+8px)] right-0 w-52 bg-white rounded-2xl shadow-[0_50px_100px_-20px_rgba(15,23,42,0.3)] border border-slate-100 p-4 z-[200] animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-center justify-between gap-3">
                      <button type="button" onClick={() => updateSearch('passengers', Math.max(1, searchState.passengers - 1))} className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-lg hover:bg-blue-50 transition-colors">-</button>
                      <span className="font-bold text-xl text-slate-900">{searchState.passengers}</span>
                      <button type="button" onClick={() => updateSearch('passengers', searchState.passengers + 1)} className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-lg hover:bg-blue-50 transition-colors">+</button>
                    </div>
                    <p className="text-[9px] text-center mt-3 font-black text-slate-300 uppercase tracking-widest">{t('searchSection.passengers')}</p>
                  </div>
                )}
              </div>

              {/* License Toggle */}
              <button
                data-cid="search-license-toggle"
                type="button"
                onClick={() => updateSearch('noLicenseOnly', !searchState.noLicenseOnly)}
                className={`flex-1 lg:w-auto flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${searchState.noLicenseOnly ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 hover:bg-white border border-transparent hover:border-blue-100'}`}
              >
                <ShieldCheck className={`w-5 h-5 shrink-0 transition-colors ${searchState.noLicenseOnly ? 'text-emerald-500' : 'text-slate-300'}`} />
                <div className="text-left">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('searchSection.license')}</p>
                  <span className={`text-sm font-bold ${searchState.noLicenseOnly ? 'text-emerald-600' : 'text-slate-800'}`}>
                    {searchState.noLicenseOnly ? t('searchSection.licenseNo') : t('searchSection.licenseAll')}
                  </span>
                </div>
              </button>

              {/* Search Button */}
              <button
                data-cid="search-submit-btn"
                type="button"
                onClick={handleSearch}
                disabled={isSearching}
                className="shrink-0 px-6 lg:px-8 py-3 rounded-2xl bg-blue-600 text-white font-black text-xs uppercase tracking-wider shadow-[0_20px_40px_-10px_rgba(37,99,235,0.4)] hover:bg-slate-950 transition-all flex items-center justify-center gap-2 active:scale-95"
              >
                {isSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                <span className="hidden sm:inline">{t('searchSection.search')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Picker Modal */}
      <DateRangePicker
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelect={handleDateSelect}
        initialStartDate={searchState.startDate}
        initialEndDate={searchState.endDate}
        minDays={1}
      />
    </>
  );
};

export default SearchSection;
